import {
  CubeIcon,
  SwatchIcon,
  TruckIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GenericChart } from "../../../Shared/components/Charts/GenericChart";
import { useQuery } from "@tanstack/react-query";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { GenericInput } from "../../../Shared/components/GenericInput";
import { GenericSelectInput } from "../../../Shared/components/GenericSelectInput";
import { ReportQuery } from "../models/ReportQuery";
import {
  fillColors,
  reportIntervalOptions,
} from "../data/report-type-interval";
import { UsersHelper } from "../helpers/UsersHelper";
import { ReportService } from "../reports/ReportService";
import { useTranslation } from "react-i18next";

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const {t} = useTranslation()
  const message =t('loading_message');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [reportInterval, setReportInterval] = useState("");
  const [reportQuery, setReportQuery] = useState<ReportQuery>({});

  const onReportInterval = (val: string) => {
    setReportInterval(val);
  };

  const onNavigate = (link: string) => {
    navigate(`/${link}`);
  };

  const applyFilter = () => {
    const queryInput = new ReportQuery();
    queryInput.interval = reportInterval;
    queryInput.startDate = startDate;
    queryInput.endDate = endDate;
    setReportQuery(queryInput);
  };

  const findAll = async () => {
    try {
      setIsLoading(true);
      const [total, chart, activity] = await Promise.all([
        ReportService.findTotal(reportQuery),
        ReportService.findChart(reportQuery),
        ReportService.findActivity(reportQuery),
      ]);
      return {
        total: total,
        chart: chart,
        activity: activity,
      };
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["dashboard", reportQuery],
    queryFn: findAll,
    enabled: !!reportQuery,
  });

  const report: any = query?.data;
  const pie = UsersHelper.mapLabelsSeries(report);

  return (
    <>
      <div className="px-6 py-4 hidden-x">
        <div className="flex justify-between">
          <div className="relative content-center">&nbsp;</div>
          <form className="mt-6" onSubmit={applyFilter}>
            <div className="flex py-3 my-2 content-end items-end">
              <GenericSelectInput
                containerClassName=""
                labelClassName=""
                label=""
                options={reportIntervalOptions}
                value={reportInterval}
                onChange={onReportInterval}
                required
                placeholder={t('select_interval_hint')}
              />
              <div className="">&nbsp;&nbsp;</div>
              <GenericInput
                containerClassName=""
                label=""
                type="date"
                value={startDate}
                onChange={setStartDate}
              />
              <div className="">&nbsp;&nbsp;</div>
              <GenericInput
                containerClassName=""
                label=""
                type="date"
                value={endDate}
                onChange={setEndDate}
              />

              <div className="">&nbsp;&nbsp;</div>
              <div
                onClick={applyFilter}
                className="px-3 py-2 text-white cursor-pointer rounded-md bg-blue-400 hover:bg-blue-600"
              >
                {t('apply')}
              </div>
            </div>
          </form>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {[
            {
              count: report?.total?.users ?? "0",
              label: t('menu_users'),
              link: "services/accounts/users",
              iconColor: "text-blue-600",
              bgColor: "bg-blue-100",
              icon: <UsersIcon className="h-4 w-4" />,
            },
            {
              count: report?.total?.companies ?? "0",
              label: t('companies'),
              link: "services/accounts/companies",
              iconColor: "text-green-600",
              bgColor: "bg-green-100",
              icon: <SwatchIcon className="h-4 w-4" />,
            },
            {
              count: report?.total?.drivers ?? "0",
              label: t('menu_drivers'),
              link: "services/vehicles/drivers",
              iconColor: "text-blue-600",
              bgColor: "bg-blue-100",
              icon: <TruckIcon className="h-4 w-4" />,
            },
            {
              count: report?.total?.vehicles ?? "0",
              label: t('menu_trucks'),
              link: "services/vehicles/home",
              iconColor: "text-red-600",
              bgColor: "bg-red-100",
              icon: <CubeIcon className="h-4 w-4" />,
            },
          ].map(({ count, link, label, iconColor, bgColor, icon }, idx) => (
            <div
              key={idx}
              onClick={() => onNavigate(link)}
              className="cursor-pointer flex items-center p-8 bg-white border rounded-lg shadow-sm hover:bg-gray-100"
            >
              <div
                className={`inline-flex flex-shrink-0 items-center justify-center h-16 w-16 ${iconColor} ${bgColor} rounded-full mr-6`}
              >
                {icon}
              </div>
              <div className="">
                <span className="block text-2xl font-bold">{count}</span>
                <span className="block text-gray-500">{label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-6 pt-4 mb-10">
        <div className="grid md:grid-cols-2 xl:grid-cols-2 gap-6">
          <div className="hidden-x">
            <GenericChart
              title={t('activity_trends')}
              type={"bar"}
              series={report?.chart?.series ?? []}
              labels={report?.chart?.data ?? []}
              fillColors={fillColors}
            />
          </div>

          <div className="hidden-x">
            <GenericChart
              title={t('trends')}
              type={"line"}
              series={report?.chart?.series ?? []}
              labels={report?.chart?.data ?? []}
              fillColors={fillColors}
            />
          </div>

          <div className="hidden-x">
            {pie?.series?.length > 0 && pie?.labels?.length > 0 && (
              <GenericChart
                type="pie"
                title={t('percentage_distribution')}
                series={pie?.series ?? []}
                labels={pie?.labels ?? []}
                fillColors={fillColors}
              />
            )}
          </div>

          <div className="hidden-x">
            {pie?.series?.length > 0 && pie?.labels?.length > 0 && (
              <GenericChart
                type="donut"
                title={t('approvals')}
                series={pie?.series ?? []}
                labels={pie?.labels ?? []}
                fillColors={fillColors}
              />
            )}
          </div>
        </div>
      </div>
      <div className="px-6 grid md:grid-cols-2 xl:grid-cols-4 gap-6 mb-20">
        {[
          {
            count: report?.activity?.activities ?? "0",
            label: t('activities'),
            link: "services/accounts/users",
            iconColor: "text-blue-600",
            bgColor: "bg-blue-100",
            icon: <UsersIcon className="h-4 w-4" />,
          },
          {
            count: report?.activity?.profile ?? "0",
            label: t('menu_profile'),
            link: "services/accounts/companies",
            iconColor: "text-green-600",
            bgColor: "bg-green-100",
            icon: <SwatchIcon className="h-4 w-4" />,
          },
          {
            count: report?.activity?.attachments ?? "0",
            label: t('attachments'),
            link: "services/vehicles/drivers",
            iconColor: "text-blue-600",
            bgColor: "bg-blue-100",
            icon: <TruckIcon className="h-4 w-4" />,
          },
          {
            count: report?.activity?.folders ?? "0",
            label: t('files'),
            link: "services/vehicles/home",
            iconColor: "text-red-600",
            bgColor: "bg-red-100",
            icon: <CubeIcon className="h-4 w-4" />,
          },
        ].map(({ count, link, label, iconColor, bgColor, icon }, idx) => (
          <div
            key={idx}
            onClick={() => onNavigate(link)}
            className="cursor-pointer flex items-center p-8 bg-white border rounded-lg shadow-sm hover:bg-gray-100"
          >
            <div
              className={`inline-flex flex-shrink-0 items-center justify-center h-16 w-16 ${iconColor} ${bgColor} rounded-full mr-6`}
            >
              {icon}
            </div>
            <div className="">
              <span className="block text-2xl font-bold">{count}</span>
              <span className="block text-gray-500">{label}</span>
            </div>
          </div>
        ))}
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
